import { useId, useRef, useState } from 'react';
import type { DragEvent } from 'react';
import type {
  AspectRatio,
  GenerationMode,
  GenerationSettings,
  Resolution,
} from '../generation/types';
import {
  ASPECT_RATIOS,
  CLIP_SECONDS,
  MODELS,
  RESOLUTIONS,
  STEP_RANGE,
  creditsToEuro,
  dimensionsFor,
  getModel,
} from '../lib/pricing';
import type { ModelId } from '../lib/pricing';
import {
  fileToDataUrl,
  formatEuroPrecise,
  formatNumber,
  formatSeconds,
} from '../lib/format';
import { IconClose, IconImage, IconSpark, IconStop, IconText } from './Icons';
import { toast } from './Toaster';

export interface UploadedImage {
  dataUrl: string;
  name: string;
}

interface ComposerProps {
  mode: GenerationMode;
  onModeChange: (mode: GenerationMode) => void;
  prompt: string;
  onPromptChange: (value: string) => void;
  image: UploadedImage | null;
  onImageChange: (image: UploadedImage | null) => void;
  settings: GenerationSettings;
  onSettingsChange: (patch: Partial<GenerationSettings>) => void;
  cost: number;
  credits: number;
  estimateSeconds: number;
  isRunning: boolean;
  canGenerate: boolean;
  onGenerate: () => void;
  onCancel: () => void;
  onTopUp: () => void;
}

const SUGGESTIONS = ['slow camera push through a foggy neon city at night'];

const MAX_IMAGE_BYTES = 12 * 1024 * 1024;

/**
 * "Steps" is a diffusion-sampling term nobody outside ML needs to learn just
 * to make a video — these three presets carry the same underlying value
 * (still what gets billed/sent to the engine) behind a plain quality label.
 */
const QUALITY_PRESETS = [
  { value: STEP_RANGE.min, label: 'Draft', hint: 'fastest' },
  { value: STEP_RANGE.reference, label: 'Standard', hint: 'balanced' },
  { value: STEP_RANGE.max, label: 'Fine', hint: 'slowest' },
] as const;

/** Highlights the closest preset even if `steps` holds an in-between value
 * (e.g. settings reused from history saved before this control existed). */
function closestQualityPreset(steps: number): number {
  return QUALITY_PRESETS.reduce((best, p) =>
    Math.abs(p.value - steps) < Math.abs(best.value - steps) ? p : best,
  ).value;
}

export function Composer(props: ComposerProps) {
  const {
    mode,
    onModeChange,
    prompt,
    onPromptChange,
    image,
    onImageChange,
    settings,
    onSettingsChange,
    cost,
    credits,
    estimateSeconds,
    isRunning,
    canGenerate,
    onGenerate,
    onCancel,
    onTopUp,
  } = props;

  const fileInput = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const promptId = useId();

  const shortOnCredits = credits < cost;
  const dims = dimensionsFor(settings.resolution, settings.aspectRatio);
  const activeModel = getModel(settings.modelId);

  const acceptFile = async (file: File | undefined | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('Only image files can be uploaded.', 'bad');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast('That image is too large (12 MB max).', 'bad');
      return;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      onImageChange({ dataUrl, name: file.name });
      toast('Image loaded.', 'good');
    } catch {
      toast('Could not read the image.', 'bad');
    }
  };

  const onDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setDragOver(false);
    void acceptFile(event.dataTransfer.files?.[0]);
  };

  return (
    <div className="composer">
      {/* ── Mode ──────────────────────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Mode</h2>
        </div>
        <div className="panel-body">
          <div className="mode-switch" role="group" aria-label="Generation mode">
            <button
              type="button"
              className="mode-btn"
              aria-pressed={mode === 'text-to-video'}
              onClick={() => onModeChange('text-to-video')}
            >
              <span className="m-title">
                <IconText size={14} /> From text
              </span>
              <span className="m-sub">text → video</span>
            </button>
            <button
              type="button"
              className="mode-btn"
              aria-pressed={mode === 'image-to-video'}
              onClick={() => onModeChange('image-to-video')}
            >
              <span className="m-title">
                <IconImage size={14} /> From image
              </span>
              <span className="m-sub">image → video</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── Prompt + image ────────────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Input</h2>
          <span className="field-hint mono">{prompt.trim().length} chars</span>
        </div>
        <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div className="field">
            <label className="field-label" htmlFor={promptId}>
              Prompt
              <span className="field-hint">
                <span className="kbd">Ctrl</span> + <span className="kbd">↵</span> to generate
              </span>
            </label>
            <textarea
              id={promptId}
              className="prompt"
              value={prompt}
              placeholder={
                mode === 'image-to-video'
                  ? 'How should the image move? e.g. “slow dolly in, grass swaying, warm afternoon light”'
                  : 'Describe the scene — what we see, how the camera moves, the quality of light…'
              }
              onChange={(e) => onPromptChange(e.target.value)}
              onKeyDown={(e) => {
                if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && canGenerate) {
                  e.preventDefault();
                  onGenerate();
                }
              }}
            />
            <div className="chip-row">
              {SUGGESTIONS.map((s) => (
                <button key={s} type="button" className="chip" onClick={() => onPromptChange(s)}>
                  {s.length > 34 ? `${s.slice(0, 33)}…` : s}
                </button>
              ))}
            </div>
          </div>

          {mode === 'image-to-video' && (
            <div className="field">
              <span className="field-label" id="img-label">
                Start image
                <span className="field-hint">required</span>
              </span>

              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => {
                  void acceptFile(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />

              {image ? (
                <figure className="image-preview">
                  <img src={image.dataUrl} alt={`Uploaded start frame: ${image.name}`} />
                  <figcaption className="ip-bar">
                    <span className="ip-name">{image.name}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-ghost"
                      onClick={() => onImageChange(null)}
                    >
                      <IconClose size={13} /> Remove
                    </button>
                  </figcaption>
                </figure>
              ) : (
                <button
                  type="button"
                  className={`dropzone ${dragOver ? 'is-over' : ''}`}
                  aria-labelledby="img-label"
                  onClick={() => fileInput.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  onPaste={(e) => {
                    const file = e.clipboardData?.files?.[0];
                    if (file) void acceptFile(file);
                  }}
                >
                  <IconImage size={22} />
                  <span className="dz-title">Drop an image here, or click to browse</span>
                  <span className="dz-sub">PNG · JPG · WEBP — 12 MB max</span>
                </button>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Settings ──────────────────────────────────────────────────────── */}
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Settings</h2>
          <span className="field-hint mono">
            {dims.width}×{dims.height}
          </span>
        </div>
        <div className="panel-body" style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Model — each carries its own per-second rate. */}
          <div className="field">
            <label className="field-label" htmlFor="model-select">
              Model
              <span className="field-hint mono">
                {activeModel.creditsPerSecond + activeModel.audioCreditsPerSecond} cr/s
              </span>
            </label>
            <select
              id="model-select"
              className="select"
              value={settings.modelId}
              onChange={(e) => onSettingsChange({ modelId: e.target.value as ModelId })}
            >
              {MODELS.map((m) => (
                <option key={m.id} value={m.id} disabled={!m.available}>
                  {m.label} · {m.vendor}
                  {m.available ? '' : ' — coming soon'}
                </option>
              ))}
            </select>
            <span className="field-hint" style={{ textTransform: 'none', letterSpacing: 0 }}>
              {activeModel.note}
            </span>
          </div>

          {/* Orientation */}
          <div className="field">
            <span className="field-label" id="aspect-label">
              Orientation
            </span>
            <div className="seg" role="group" aria-labelledby="aspect-label">
              {ASPECT_RATIOS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  className="seg-btn"
                  aria-pressed={settings.aspectRatio === a.value}
                  onClick={() => onSettingsChange({ aspectRatio: a.value as AspectRatio })}
                >
                  <span className="aspect-row">
                    <span
                      className={`aspect-glyph ${a.value === 'portrait' ? 'is-portrait' : ''}`}
                      aria-hidden
                    />
                    {a.label}
                  </span>
                  <span className="s-sub">{a.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="field-label" id="res-label">
              Resolution
            </span>
            <div className="seg" role="group" aria-labelledby="res-label">
              {RESOLUTIONS.map((r) => (
                <button
                  key={r.value}
                  type="button"
                  className="seg-btn"
                  aria-pressed={settings.resolution === r.value}
                  onClick={() => onSettingsChange({ resolution: r.value as Resolution })}
                >
                  {r.label}
                  <span className="s-sub">{r.detail}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span className="field-label" id="quality-label">
              Quality
            </span>
            <div className="seg" role="group" aria-labelledby="quality-label">
              {QUALITY_PRESETS.map((q) => (
                <button
                  key={q.value}
                  type="button"
                  className="seg-btn"
                  aria-pressed={closestQualityPreset(settings.steps) === q.value}
                  onClick={() => onSettingsChange({ steps: q.value })}
                >
                  {q.label}
                  <span className="s-sub">{q.hint}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="toggle-row">
            <span className="toggle-copy">
              <span className="t-title">Model caching</span>
              <span className="t-sub">Keep the model resident between runs — faster, uses more VRAM.</span>
            </span>
            <button
              type="button"
              className="toggle"
              role="switch"
              aria-checked={settings.cacheEnabled}
              aria-label="Model caching"
              onClick={() => onSettingsChange({ cacheEnabled: !settings.cacheEnabled })}
            />
          </div>
        </div>
      </section>

      {/* ── Generate ──────────────────────────────────────────────────────── */}
      <div className="generate-block">
        <div className={`cost-line ${shortOnCredits ? 'is-short' : ''}`}>
          <span>
            {CLIP_SECONDS}s clip · ~{formatSeconds(estimateSeconds)}
          </span>
          <span>
            <span className="cost-value">{formatNumber(cost)}</span> cr
            <span style={{ opacity: 0.6 }}> · {formatEuroPrecise(creditsToEuro(cost))}</span>
          </span>
        </div>

        {isRunning ? (
          <button type="button" className="btn btn-danger btn-lg btn-block" onClick={onCancel}>
            <IconStop size={16} /> Cancel
          </button>
        ) : shortOnCredits ? (
          <button type="button" className="btn btn-lg btn-block" onClick={onTopUp}>
            Not enough credits — top up
          </button>
        ) : (
          <button
            type="button"
            className="btn btn-primary btn-lg btn-block"
            disabled={!canGenerate}
            onClick={onGenerate}
          >
            <IconSpark size={17} /> Generate
          </button>
        )}

        <p className="hint-line">
          {mode === 'image-to-video' && !image
            ? 'Upload a start image to generate.'
            : !prompt.trim()
              ? 'Write a prompt to generate.'
              : `Balance after this run: ${formatNumber(Math.max(0, credits - cost))} credits`}
        </p>
      </div>
    </div>
  );
}
